import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { CommonModule, NgStyle } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { IconModule } from '@coreui/icons-angular';
import {
  CollapseModule,
  NavbarModule,
  NavModule,
  ButtonModule,
  FormModule,
  CardModule,
  GridModule,
  DropdownModule
} from '@coreui/angular';
import { UserService } from './../../../service/user.service';
import { AuthService } from '../../../service/auth.service';
import { ApplicationSessionStateService } from '../../../service/application-session-state.service';
import { clearLocalStorageSession } from '../../../service/local-storage-session.util';
import { TranslateModule } from '@ngx-translate/core';
import { HomeFooterComponent } from '../home-footer/home-footer.component';
import { TokenStorageService } from '@services/token-storage.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserPreferences } from '../../../service/user-preferences.model';
import { extractTeamCodeFromRecentPage } from '../../../service/user-preferences.model';

interface HomeHeaderUser {
  firstname: string;
  lastname: string;
  email: string;
  recent_page?: string;
  user_preferences?: UserPreferences;
}

@Component({
  selector: 'app-home-header',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    RouterModule,
    HomeFooterComponent,
    NgStyle,
    IconModule,
    CollapseModule,
    NavbarModule,
    NavModule,
    ButtonModule,
    FormModule,
    CardModule,
    GridModule,
    DropdownModule,
    TranslateModule,
  ],
  templateUrl: './home-header.component.html',
  styleUrl: './home-header.component.scss'
})
export class HomeHeaderComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  userMenuOpen = false;
  profilePicture = '';
  user: HomeHeaderUser = {
    firstname: '',
    lastname: '',
    email: '',
  };

  private avatarSub?: Subscription;
  private routerSub?: Subscription;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private applicationSessionState: ApplicationSessionStateService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.syncAuthState();
    this.routerSub = this.router.events
      .pipe(filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd))
      .subscribe(() => this.syncAuthState());
  }

  ngOnDestroy(): void {
    this.avatarSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  get defaultAvatarUrl(): string {
    return this.userService.defaultAvatarUrl;
  }

  get displayName(): string {
    const full = `${this.user.firstname || ''} ${this.user.lastname || ''}`.trim();
    return full || this.user.email || '';
  }

  get displayInitial(): string {
    const src = this.user.firstname || this.user.email || '?';
    return src.slice(0, 1).toUpperCase();
  }

  private syncAuthState(): void {
    const hasToken = TokenStorageService.hasToken();
    if (!hasToken) {
      this.isLoggedIn = false;
      this.user = { firstname: '', lastname: '', email: '' };
      this.profilePicture = this.defaultAvatarUrl;
      this.avatarSub?.unsubscribe();
      return;
    }
    if (this.isLoggedIn && this.user.email) {
      return;
    }
    this.isLoggedIn = true;
    this.loadSelfInfo();
  }

  private loadSelfInfo(): void {
    this.userService.get_self_info().subscribe({
      next: (data) => {
        if (!data?.user) {
          this.isLoggedIn = false;
          return;
        }
        if (data?.token) {
          TokenStorageService.refreshToken(data.token);
        }
        this.user = {
          firstname: data.user.firstname || '',
          lastname: data.user.lastname || '',
          email: data.user.email || '',
          recent_page: data.user.recent_page,
          user_preferences: data.user.user_preferences,
        };
        this.refreshPersonalAvatar();
      },
      error: () => {
        this.isLoggedIn = TokenStorageService.hasToken();
      },
    });
  }

  private refreshPersonalAvatar(force = false): void {
    this.avatarSub?.unsubscribe();
    this.avatarSub = this.userService.loadPersonalAvatarUrl(force).subscribe({
      next: (url) => {
        this.profilePicture = url;
      },
      error: () => {
        this.profilePicture = this.defaultAvatarUrl;
      },
    });
  }

  onAvatarImageError(): void {
    this.profilePicture = this.defaultAvatarUrl;
  }

  goToWorkspace(): void {
    this.userMenuOpen = false;
    void this.router.navigate(this.resolveWorkspaceCommands());
  }

  private resolveWorkspaceCommands(): string[] {
    const prefTeamCode =
      this.user.user_preferences?.selected_team_code ||
      extractTeamCodeFromRecentPage(this.user.recent_page);
    if (prefTeamCode) {
      return ['/team', prefTeamCode, 'home'];
    }
    const recentPage = this.user.recent_page;
    if (recentPage && typeof recentPage === 'string' && recentPage.startsWith('team/')) {
      const parts = recentPage.split('/');
      if (parts.length >= 2 && parts[1]) {
        return ['/team', parts[1], 'home'];
      }
    }
    return ['/home'];
  }

  onLogout(): void {
    this.userMenuOpen = false;
    this.applicationSessionState.resetForLogout();
    clearLocalStorageSession();
    this.authService.logout();
    this.isLoggedIn = false;
    this.user = { firstname: '', lastname: '', email: '' };
    this.profilePicture = this.defaultAvatarUrl;
    void this.router.navigate(['/']);
  }
}
